import { useCallback } from 'react';

import { useMarkCareerLessonDoneMutation } from 'core/graphql';

/** Field name the backend reports a missing/invalid artifact link on. */
export const ARTIFACT_URL_FIELD = 'artifactUrl';

export interface ICompleteCareerLessonResult {
  success: boolean;
  /** Backend error code, if any (e.g. ARTIFACT_REQUIRED). */
  errorCode: string | null;
  /** Which input the error belongs to — `artifactUrl` or null for a form-level error. */
  errorField: string | null;
  /** Server-worded message; the page falls back to a translated string. */
  errorMessage: string | null;
}

/**
 * Marks a career lesson as passed.
 *
 * Refetches BOTH career operations by name on completion: `CareerLessonsList`
 * so the next lesson's freshly-granted `isUnlocked` arrives, and
 * `MyCareerCenterInfo` so the landing progress bar and the lesson counter move.
 * The refetch runs even on a failed mutation — a customError such as "already
 * completed" means our cached state is the stale thing, exactly as
 * useSubmitSurveyResponse handles ALREADY_SUBMITTED.
 *
 * Repeat submits are safe: the backend's unique (member, lesson) progress row
 * is the double-submit guard, so a double click cannot create a second record.
 *
 * Network errors propagate as a thrown ApolloError — callers own the retry UX.
 */
export const useCompleteCareerLesson = () => {
  const [mutate, { loading }] = useMarkCareerLessonDoneMutation({
    refetchQueries: ['CareerLessonsList', 'MyCareerCenterInfo'],
    // Wait for the refetches so the caller can navigate/redraw against fresh
    // unlock state instead of the stale cached lock.
    awaitRefetchQueries: true,
  });

  const completeLesson = useCallback(
    async (lessonId: string, artifactUrl?: string): Promise<ICompleteCareerLessonResult> => {
      const trimmed = artifactUrl?.trim();

      const { data } = await mutate({
        variables: { lessonId, artifactUrl: trimmed || null },
      });

      const payload = data?.completeCareerLesson;
      const firstError = payload?.customErrors?.[0] ?? null;

      return {
        success: Boolean(payload?.success),
        errorCode: firstError?.code ?? null,
        errorField: firstError?.field ?? null,
        errorMessage: firstError?.message ?? null,
      };
    },
    [mutate],
  );

  return { completeLesson, loading };
};
